import {useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClock} from '@fortawesome/free-solid-svg-icons';

const COUNTDOWN_TICK_INTERVAL_MS = 1000;

const secondsUntil = (resetsAt?: string): number | null => {
    if (!resetsAt) return null;

    const timestamp = Date.parse(resetsAt);
    if (!Number.isFinite(timestamp)) return null;

    return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
};

const formatCountdown = (secondsLeft: number): string => {
    const safeSeconds = Math.max(0, secondsLeft);
    const hours = Math.floor(safeSeconds / 3600)
        .toString()
        .padStart(2, '0');
    const minutes = Math.floor((safeSeconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
    const seconds = Math.floor(safeSeconds % 60)
        .toString()
        .padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
};

type MidnightCountdownProps = {
    resetsAt?: string;
    className?: string;
    label?: string;
};

export const MidnightCountdown = ({
    resetsAt,
    className,
    label,
}: MidnightCountdownProps) => {
    const [secondsLeft, setSecondsLeft] = useState<number | null>(() =>
        secondsUntil(resetsAt)
    );
    useEffect(() => {
        const update = () => setSecondsLeft(secondsUntil(resetsAt));
        update();

        const interval = window.setInterval(
            update,
            COUNTDOWN_TICK_INTERVAL_MS
        );
        return () => window.clearInterval(interval);
    }, [resetsAt]);

    const countdownText = useMemo(() => {
        if (secondsLeft === null) return '--:--:--';
        return formatCountdown(secondsLeft);
    }, [secondsLeft]);

    return (
        <span className={className}>
            {label ? (
                <>{label} </>
            ) : (
                <FontAwesomeIcon icon={faClock} className="me-1" />
            )}
            {countdownText}
        </span>
    );
};
